<script lang="ts">
  import axios from "axios";
  import {
    book,
    bookingConfirmation,
    step,
  } from "../../stores/WalkinBookingStore";

  import {
    hasGroupon,
    validGroupon,
    grouponCode,
    grouponInfo,
    location,
    type,
    bypassPayment,
  } from "../../stores/BookingStore";

  import SQPayment from "../../../../v2/src/js/modules/SQPayment";
  import * as payment from "../../../../v2/src/js/modules/payment.js";
  import { checkNonTaxedLocation } from "../../helpers/BookHelper";

  import { convert24HoursFormat } from "../../helpers";

  export let parentEl: string = "walkin-booking";
  export let walkinDate: string = "";

  let validation: any = {
    promocode: "",
  };

  let sqPayment: SQPayment | null = null;
  let admission: string = "--";
  let fullName: string = "";
  let email: string = "";
  let phone: string = "";
  let localData: any = {};
  let admissionCharges: string = "00.00";
  let admissionChargesQty: string = "1";
  let tax: string = "00.00";
  let totalDue: string = "00.00";

  let nonceValue: string;
  let verificationToken: string;

  let paymentErrors: any = [];
  let paymentSuccess = false;
  let paymentError = "";
  let isProcessing = false;

  // Groupon
  let showGroupon: boolean = false;
  let grouponInformation: any = {};
  let selectedLocation: string;
  let selectedCountry: string;
  let bookingType: string;
  let userBook: any;
  let hasValidGroupon: boolean = false;

  let touched = {
    fullName: false,
    email: false,
    phone: false,
  };

  let errors = {
    fullName: "",
    email: "",
    phone: "",
  };

  let isValid = false;

  let previousBookingData = {
    location: "",
    admission: "",
    timeslot: "",
    type: "",
    hasValidGroupon: false,
    grouponCode: null,
  };

  let hasPaymentBypassed: boolean = false;

  // Subscribe to store changes
  book.subscribe((value) => {
    localData = value;
    userBook = value;
    if (value?.customer) {
      fullName = value.customer.fullName || "";
      email = value.customer.email || "";
      phone = formatPhoneNumber(value.customer.phone || "");
      // Only validate if fields have been touched
      if (touched.fullName || touched.email || touched.phone) {
        validateAllFields();
      }
    }
    if (value?.admission) {
      admission = value.admission;
    }
  });

  location.subscribe((value) => {
    selectedCountry = value.country;
    selectedLocation = value.name;
  });

  type.subscribe((value) => {
    bookingType = value;
  });

  validGroupon.subscribe((value) => {
    hasValidGroupon = value;
  });

  hasGroupon.subscribe((value) => (showGroupon = value));

  grouponInfo.subscribe((value) => {
    grouponInformation = value;
  });

  bypassPayment.subscribe((value) => (hasPaymentBypassed = value));

  function formatPhoneNumber(value: string): string {
    // Strip all non-digits
    const digits = value.replace(/\D/g, "");

    // Format based on length
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  }

  function validateFullName(name: string): string {
    if (!name.trim()) return "Full name is required";
    if (name.trim().length < 2)
      return "Full name must be at least 2 characters";
    if (!/^[a-zA-Z]+(?:[\s'-][a-zA-Z]+)*$/.test(name.trim()))
      return "Please enter a valid full name";
    return "";
  }

  function validateEmail(email: string): string {
    if (!email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return "Please enter a valid email address";
    return "";
  }

  function validatePhone(phone: string): string {
    const digits = phone.replace(/\D/g, "");
    if (!digits) return "Phone number is required";
    if (digits.length !== 10) return "Please enter a 10-digit phone number";
    return "";
  }

  function validateAllFields() {
    // Only validate touched fields
    if (touched.fullName) validateField("fullName", fullName);
    if (touched.email) validateField("email", email);
    if (touched.phone) validateField("phone", phone);

    updateFormValidity();
  }

  function updateFormValidity() {
    // Update form validity - only consider touched fields
    isValid =
      (!touched.fullName || !errors.fullName) &&
      (!touched.email || !errors.email) &&
      (!touched.phone || !errors.phone) &&
      (touched.fullName || touched.email || touched.phone); // At least one field must be touched
  }

  function validateField(field: "fullName" | "email" | "phone", value: string) {
    if (!touched[field]) return; // Only validate if field has been touched

    switch (field) {
      case "fullName":
        errors.fullName = validateFullName(value);
        break;
      case "email":
        errors.email = validateEmail(value);
        break;
      case "phone":
        errors.phone = validatePhone(value);
        break;
    }
    errors = errors; // Trigger Svelte reactivity

    // Update form validity without recursion
    updateFormValidity();
  }

  function handleCustomerInfoChange(
    field: "fullName" | "email" | "phone",
    value: string
  ) {
    touched[field] = true;
    touched = touched; // Trigger Svelte reactivity

    let processedValue = value;
    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
      phone = processedValue; // Update the displayed value
    }

    validateField(field, processedValue);

    // Update store if validation passes or field hasn't been touched
    if (!touched[field] || !errors[field]) {
      book.update((storeValue) => ({
        ...storeValue,
        customer: {
          ...storeValue.customer,
          [field]: field === "phone" ? value.replace(/\D/g, "") : value,
        },
      }));
    }
  }

  // Watch for changes in form fields - simplified to avoid recursion
  $: if (fullName || email || phone) {
    updateFormValidity();
  }

  function isFormValid(): boolean {
    // Form is only valid if all fields are touched and valid
    return isValid && touched.fullName && touched.email && touched.phone;
  }

  $: {
    if (localData.locationCountry && localData.locationName && !sqPayment) {
      let location = localData.locationName.toLowerCase();
      let country = localData.locationCountry.toLowerCase();
      if (country.toLowerCase() === "canada") {
        country = "ca";
      }

      sqPayment = new SQPayment({
        location: location,
        country: country,
        env: "prod",
        cardContainer: "#card-container",
      });
      sqPayment.init(parentEl);
    }
  }

  // Create derived values for booking data
  $: bookingData = {
    location: localData.location || "",
    admission: localData.admission || "",
    timeslot: localData.timeslot || "",
    type: localData.type || "",
    hasValidGroupon,
    grouponCode: grouponInformation.code ?? null,
  };

  $: {
    if (localData.admission === "3") {
      hasGroupon.update((value) => false);
    }
  }

  // Watch only booking-related data changes
  $: localData.location,
    localData.admission,
    localData.timeslot,
    localData.type,
    hasValidGroupon,
    grouponInformation,
    getPaymentInformation();

  const getPaymentInformation = () => {
    const currentBookingData = {
      location: localData.location || "",
      admission: localData.admission || "",
      timeslot: localData.timeslot || "",
      type: localData.type || "",
      hasValidGroupon,
      grouponCode: grouponInformation.code ?? null,
    };

    // Only fetch if values have changed and all are present
    const hasChanged =
      currentBookingData.location !== previousBookingData.location ||
      currentBookingData.admission !== previousBookingData.admission ||
      currentBookingData.timeslot !== previousBookingData.timeslot ||
      currentBookingData.type !== previousBookingData.type ||
      currentBookingData.hasValidGroupon !==
        previousBookingData.hasValidGroupon ||
      currentBookingData.grouponCode !== previousBookingData.grouponCode;

    const allFieldsPresent =
      currentBookingData.location &&
      currentBookingData.admission &&
      currentBookingData.timeslot &&
      currentBookingData.type;

    if (hasChanged && allFieldsPresent) {
      const bookingDataInformation = {
        location: currentBookingData.location,
        locationName: localData.locationName,
        admissions: currentBookingData.admission,
        timeslot: currentBookingData.timeslot,
        type: currentBookingData.type,
        group: localData.admission,
        has_valid_groupon: hasValidGroupon,
        gropon_information: grouponInformation ? grouponInformation : null,
      };

      axios
        .get("/wp-json/badaxe/v1/location/walkin-payment-information", {
          params: bookingDataInformation,
        })
        .then((response) => {
          admissionCharges = response.data.admission_charges;
          tax = response.data.total_taxes;
          totalDue = response.data.total_amount;
        })
        .catch((error) => {
          console.error("Error sending booking info:", error);
        });

      // Update previous values after successful fetch
      previousBookingData = { ...currentBookingData };
    }
  };

  // No payment handler
  const processNoPayment = () => {
    if (hasPaymentBypassed) {
      const fullDate = `${walkinDate.getFullYear()}-${walkinDate.getMonth() + 1}-${walkinDate.getDate()} ${localData.timeslot}`;

      const firstName = fullName.split(" ")[0];
      const lastName = fullName.split(" ")[1] || "";

      const params = {
        location: localData.location,
        locationName: localData.locationName,
        admissions: localData.admission,
        timeslot: localData.timeslot,
        type: localData.type,
        fullName: fullName,
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
        totalDue: totalDue,
        duration: localData.duration,
        date:
          walkinDate != ""
            ? new Date(walkinDate).toISOString().split("T")[0]
            : "",
        has_valid_groupon: hasValidGroupon,
        groupon_information: grouponInformation,
      };

      payment
        .processWalkinBookingWithoutPayment(
          {
            location: localData.location,
            locationName: localData.locationName,
            admissions: localData.admission,
            timeslot: localData.timeslot,
            type: localData.type,
            nonce: nonceValue,
            verificationToken: verificationToken,
            cardBrand: null,
            cardLast4: null,
            fullName: fullName,
            firstName: firstName,
            lastName: lastName,
            email: email,
            phone: phone,
            totalDue: totalDue,
            duration: localData.duration,
            date:
              walkinDate != ""
                ? new Date(walkinDate).toISOString().split("T")[0]
                : "",
            has_valid_groupon: hasValidGroupon,
            groupon_information: grouponInformation,
          },
          (response: any) => {
            // Reset processing state
            isProcessing = false;

            // Handle payment response
            if (response && !response.errors) {
              // Payment successful
              paymentSuccess = true;

              book.update((storeValue) => ({
                ...storeValue,
                paymentProcessed: true,
                customer: {
                  firstName: firstName,
                  lastName: lastName,
                  fullName: fullName,
                  email: email,
                  phone: phone,
                },
              }));

              bookingConfirmation.update((storedValue) => ({
                ...storedValue,
                reference: response.reference,
                secureToken: response.secure_token,
              }));

              step.update((s) => s + 1);

              paymentError = "";
            } else {
              // Payment failed
              paymentError = "Payment failed. Please try again.";
              if (response && response.errors) {
                console.error("Payment errors:", response.errors);
              }
            }
          }
        )
        .catch((e: any) => {
          console.error("Error verifying buyer: ", e);
          paymentError = "Error verifying buyer. Please try again.";
          isProcessing = false; // Reset processing state
        });
      // }
    }
  };
  // Pay Handler
  const onProcessPayment = (e: any) => {
    e.preventDefault();

    // Set processing state to true
    isProcessing = true;

    if (hasPaymentBypassed) {
      processNoPayment();
      return;
    }

    sqPayment
      .paymentEventHandler(null, "card")
      .then((nonce: any) => {
        if (typeof nonce.errors !== "undefined") {
          // Handle payment errors
          paymentError = "Payment declined. Please check your card details.";
          isProcessing = false; // Reset processing state

          nonce.errors.forEach((sqError: any) => {
            console.error("Square Error:", sqError.message);
          });
          return;
        }

        nonceValue = nonce;

        const firstName = fullName.split(" ")[0];
        const lastName = fullName.split(" ")[1] || "";

        // Verify Buyer with the response token
        sqPayment.setIntent("CHARGE");
        sqPayment.setCity(localData.locationName);
        sqPayment.setEmail(email);
        sqPayment.setGivenName(firstName);
        sqPayment.setFamilyName(lastName);
        sqPayment.setPhone(phone);
        sqPayment.setTotalAmount(totalDue);

        sqPayment
          .getPayments()
          .verifyBuyer(nonce, sqPayment.verificationDetails())
          .then((result: any) => {
            verificationToken = result.token;
            nonceValue = nonce;

            payment.processWalkinBookingPayment(
              {
                location: localData.location,
                locationName: localData.locationName,
                admissions: localData.admission,
                group: localData.admission,
                timeslot: localData.timeslot,
                type: localData.type,
                nonce: nonceValue,
                verificationToken: verificationToken,
                cardBrand: null,
                cardLast4: null,
                fullName: fullName,
                firstName: firstName,
                lastName: lastName,
                email: email,
                phone: phone,
                totalDue: totalDue,
                duration: localData.duration,
                date:
                  walkinDate != ""
                    ? new Date(walkinDate).toISOString().split("T")[0]
                    : "",
                has_valid_groupon: hasValidGroupon,
                groupon_information: grouponInformation,
              },
              (response: any) => {
                // Reset processing state
                isProcessing = false;

                // Handle payment response
                if (response && !response.errors) {
                  // Payment successful
                  paymentSuccess = true;

                  book.update((storeValue) => ({
                    ...storeValue,
                    paymentProcessed: true,
                    customer: {
                      firstName: firstName,
                      lastName: lastName,
                      fullName: fullName,
                      email: email,
                      phone: phone,
                    },
                  }));

                  bookingConfirmation.update((storedValue) => ({
                    ...storedValue,
                    reference: response.reference,
                    secureToken: response.secure_token,
                  }));

                  step.update((s) => s + 1);

                  paymentError = "";
                } else {
                  // Payment failed
                  paymentError = "Payment failed. Please try again.";
                  if (response && response.errors) {
                    console.error("Payment errors:", response.errors);
                  }
                }
              }
            );
          })
          .catch((e: any) => {
            console.error("Error verifying buyer: ", e);
            paymentError = "Error verifying buyer. Please try again.";
            isProcessing = false; // Reset processing state
          });
      })
      .catch((e: any) => {
        paymentError = "Error processing payment. Please try again.";
        isProcessing = false; // Reset processing state
      });
  };

  $: if (paymentSuccess) {
    // setTimeout(() => {
    // paymentSuccess = false;
    // Reset all booking data
    // book.set({
    //   location: "",
    //   locationName: "",
    //   admission: "",
    //   timeslot: "",
    //   type: "",
    //   customer: {
    //     fullName: "",
    //     email: "",
    //     phone: "",
    //   },
    // });
    // Reset local variables
    // fullName = "";
    // email = "";
    // phone = "";
    // admission = "--";
    // admissionCharges = "--";
    // tax = "--";
    // totalDue = "--";
    // Reset validation state
    // touched = {
    //   fullName: false,
    //   email: false,
    //   phone: false,
    // };
    // errors = {
    //   fullName: "",
    //   email: "",
    //   phone: "",
    // };
    // isValid = false;
    // }, 3000);
  }

  const onPromocodeChangeHandler = (e: any) => {
    const promocode = e.target.value;

    if (promocode.length === 0) {
      validGroupon.set(false);
      return;
    } else if (promocode.length > 1 && promocode.length < 6) {
      setTimeout(() => {
        validation.promocode = "Promocode cannot be less than 6 characters";
      }, 3000);
      validGroupon.set(false);
      return;
    } else {
      setTimeout(() => {
        validation.promocode = "";
      }, 3000);
    }

    // check if it's valid promocode
    let checkLocation = selectedLocation.toLowerCase();

    // Walk-ins are always social lane bookings
    const isSocialLane = true;

    if (
      selectedLocation.toLowerCase() !== "charlotte" &&
      selectedLocation.toLowerCase() !== "long beach" &&
      selectedLocation.toLowerCase() !== "windsor"
    ) {
      checkLocation = selectedCountry.toLowerCase();
    }

    axios
      .post(`/wp-json/badaxe/v1/groupon`, {
        location: checkLocation.toLowerCase(),
        groupon: promocode.toLowerCase(),
        type: bookingType,
        group: userBook.admission,
        is_social_lane: isSocialLane,
      })
      .then((response) => {
        const grouponResponse = response.data;

        if (grouponResponse.success) {
          validation.promocode = "";
          validGroupon.set(true);
          grouponInfo.update((obj) => {
            let o = { ...obj };
            o.code = grouponResponse.data.groupon_code;
            o.length = grouponResponse.data.length;
            o.location = grouponResponse.data.location;
            o.people = grouponResponse.data.people;
            o.type = grouponResponse.data.type;
            return o;
          });
          grouponCode.set(grouponResponse.groupon_code);

          if (checkNonTaxedLocation(selectedLocation)) {
            bypassPayment.update((value) => true);
          }
        } else {
          validation.promocode = "Promo Code is not valid for this location";
          validGroupon.set(false);
          grouponInfo.update((obj) => {
            let o = { ...obj };
            o.code = null;
            o.length = null;
            o.location = null;
            o.people = null;
            o.type = null;
            return o;
          });
          grouponCode.set(null);
          bypassPayment.update((value) => false);
        }
      });
  };
</script>

<div class="w-full flex flex-col items-center justify-start">
  <div class="w-full relative pl-4">
    <div class="absolute top-0 left-0 w-1 h-full bg-brand"></div>
    <div
      class="flex flex-col items-start justify-center border-b-2 border-gray-200 py-2"
    >
      <p class="uppercase">Location</p>
      <p class="text-lg text-brand uppercase font-semibold">
        {localData.locationName}
      </p>
    </div>
    <div
      class="flex flex-col items-start justify-center border-b-2 border-gray-200 py-2"
    >
      <p class="uppercase">Admissions</p>
      <p class="text-lg text-brand uppercase font-semibold">
        {#if !hasValidGroupon}
          {localData.admission}
        {:else}
          {grouponInformation.people}
        {/if}
      </p>
    </div>
    <div
      class="flex flex-col items-start justify-center border-b-2 border-gray-200 py-2"
    >
      <p class="uppercase">Social Lane</p>
      <p class="text-xl text-brand uppercase font-semibold">
        {localData.duration} Minutes
      </p>
      {#if localData.type === "throw-em-all"}
        <p class="text-base text-brand uppercase">Throw 'em All</p>
        <p>Hatchet, Knife, Big Axe</p>
      {/if}
      {#if localData.type === "30m-hatchet"}
        <p class="text-base text-brand uppercase">Hatchet</p>
      {/if}
      {#if localData.type === "30m-knife"}
        <p class="text-base text-brand uppercase">Knife</p>
      {/if}
    </div>
    <div class="flex flex-col items-start justify-center py-2">
      <p class="uppercase">Time slot</p>
      <p class="text-lg text-brand uppercase font-semibold">
        {convert24HoursFormat(localData.timeslot)}
      </p>
    </div>
    {#if walkinDate != ""}
      <div class="flex flex-col items-start justify-center py-2">
        <p class="uppercase">Date</p>
        <p class="text-lg text-brand uppercase font-semibold">
          {walkinDate
            ? new Date(walkinDate)
                .toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                .replace(/,\s(\d{4})$/, ", $1")
            : ""}
        </p>
      </div>
    {/if}
    {#if hasValidGroupon}
      <div
        class="flex flex-col items-start justify-center border-b-2 border-gray-200 py-2"
      >
        <p class="uppercase">Groupon</p>
        <p class="text-lg text-brand uppercase font-semibold">
          {grouponInformation.code}
        </p>
      </div>
    {/if}
  </div>
  <div class="w-full flex flex-col items-start justify-start mt-8">
    <h3 class="uppercase text-lg font-semibold mb-4">Billing Information</h3>
    <div class="w-full mb-4">
      <input
        type="text"
        name="full_name"
        placeholder="Full Name *"
        class="w-full rounded-md {touched.fullName && errors.fullName
          ? 'border-red-500'
          : ''}"
        bind:value={fullName}
        on:input={() => handleCustomerInfoChange("fullName", fullName)}
        on:blur={() => handleCustomerInfoChange("fullName", fullName)}
      />
      {#if touched.fullName && errors.fullName}
        <p class="text-red-500 text-sm mt-1">{errors.fullName}</p>
      {/if}
    </div>
    <div class="w-full mb-4">
      <input
        type="email"
        name="email"
        placeholder="Email *"
        class="w-full rounded-md {touched.email && errors.email
          ? 'border-red-500'
          : ''}"
        bind:value={email}
        on:input={() => handleCustomerInfoChange("email", email)}
        on:blur={() => handleCustomerInfoChange("email", email)}
      />
      {#if touched.email && errors.email}
        <p class="text-red-500 text-sm mt-1">{errors.email}</p>
      {/if}
    </div>
    <div class="w-full mb-4">
      <input
        type="tel"
        name="phone"
        placeholder="Phone * (123) 456-7890"
        class="w-full rounded-md {touched.phone && errors.phone
          ? 'border-red-500'
          : ''}"
        bind:value={phone}
        on:input={() => handleCustomerInfoChange("phone", phone)}
        on:blur={() => handleCustomerInfoChange("phone", phone)}
      />
      {#if touched.phone && errors.phone}
        <p class="text-red-500 text-sm mt-1">{errors.phone}</p>
      {/if}
    </div>

    {#if showGroupon}
      <div class="w-full mb-4">
        <input
          type="text"
          placeholder="Discount/Promo Code"
          class="text-black dark:text-black rounded mb-2 w-full h-12 border-2 border-gray-400"
          on:keyup={onPromocodeChangeHandler}
        />
        {#if validation.promocode !== ""}
          <p class="text-brand text-xs mb-2">{validation.promocode}</p>
        {/if}
      </div>
    {/if}
  </div>
  {#if !hasPaymentBypassed}
    <div class="w-full flex flex-col items-start justify-start">
      <h3 class="uppercase text-lg font-semibold">Credit Card Information</h3>
      <div id={`card-container`} class={`rounded mb-2 w-full`}></div>
    </div>
  {/if}

  <div class="w-full">
    {#if hasValidGroupon}
      <div class="w-full flex flex-row px-2 py-2 bg-gray-200">
        <span class="flex-1">{grouponInformation.people} admissions total</span>
        <span class="w-20">${admissionCharges}</span>
      </div>
    {:else}
      <div class="w-full flex flex-row px-2 py-2 bg-gray-200">
        <span class="flex-1">Deposit ({admissionChargesQty} admission)</span>
        <span class="w-20">${admissionCharges}</span>
      </div>
    {/if}

    <div class="w-full flex flex-row px-2 py-2">
      <span class="flex-1">Tax</span>
      <span class="w-20">${tax}</span>
    </div>
    <div class="w-full flex flex-row px-2 py-2 bg-gray-200">
      <span class="flex-1">Total Due</span>
      <span class="w-20">${totalDue}</span>
    </div>
    <div class="w-full mt-4">
      <button
        on:click={onProcessPayment}
        class="px-8 py-4 text-white {isValid && !isProcessing
          ? 'bg-brand'
          : 'bg-gray-400 cursor-not-allowed'} inline-block w-full rounded-full"
        disabled={!isValid || isProcessing}
      >
        {#if isProcessing}
          Processing Payment...
        {:else}
          Pay and Confirm Order
        {/if}
      </button>
    </div>
    {#if paymentError}
      <div
        class="w-full px-4 py-3 mt-4 bg-red-100 border border-red-400 text-red-700 rounded"
      >
        {paymentError}
      </div>
    {/if}
  </div>
</div>

<style></style>
